<template>
  <div class="s-upload-preview">
    <!-- 关闭文件列表时，用内置缩略图回显（替代原先 el-upload + 手写 img 的写法） -->
    <div
      v-for="(item, index) in currentList"
      :key="item.url + index"
      class="s-upload-preview__item"
    >
      <img class="s-upload-preview__img" :src="item.url" :alt="item.name" />
      <span class="s-upload-preview__actions">
        <i class="el-icon-view" @click="handlePreviewItem(item)"></i>
        <i
          class="el-icon-delete2"
          v-if="!disabled"
          @click="removeAt(index)"
        ></i>
      </span>
    </div>

    <!-- 上传入口：与缩略图同尺寸的方块，达到 limit 后整体隐藏 -->
    <div class="s-upload-preview__item" v-if="!isLimitReached">
      <el-upload class="s-upload" ref="upload" v-bind="uploadProps">
        <!-- 外部自定义了触发内容时优先使用，否则走内置默认 UI -->
        <slot v-if="$slots.default"></slot>
        <i v-if="!$slots.default && isPictureCard" class="el-icon-plus"></i>
        <div
          v-if="!$slots.default && !isPictureCard"
          class="s-upload-trigger__inner"
        >
          <i class="el-icon-upload"></i>
          <div class="s-upload-default__text">
            <template v-if="isDrag">将文件拖到此处，或</template>
            <span class="s-upload-default__link">点击上传</span>
          </div>
        </div>
        <template v-if="$slots.trigger" v-slot:trigger>
          <slot name="trigger"></slot>
        </template>
        <template v-if="$slots.tip" v-slot:tip>
          <slot name="tip"></slot>
        </template>
      </el-upload>
    </div>

    <!-- 内置图片预览弹窗：外部未传 on-preview 时兜底（同 UploadImg.vue 的做法） -->
    <el-dialog
      :title="previewDialog.name || '预览'"
      size="large"
      :modal-append-to-body="false"
      :visible.sync="previewDialog.visible"
      custom-class="s-upload-preview-dialog"
    >
      <div class="s-upload-preview-body">
        <img
          v-if="previewDialog.url"
          :src="previewDialog.url"
          :alt="previewDialog.name"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script>
/**
 * SUpload —— 基于 el-upload 1.4 的通用上传组件（已全局注册）
 *
 * 1. 透传 el-upload 全部属性：action / list-type / accept / multiple / drag /
 *    headers / data / name / with-credentials / show-file-list / auto-upload /
 *    disabled / type / http-request / on-change / on-progress / on-preview ...
 *    用法与原 el-upload 完全一致（这些回调会原样透传，不会被覆盖）。
 * 2. size：单个文件大小上限，单位 KB，0/不传表示不限制，超限会提示并阻断上传。
 * 3. v-model：文件列表，数组元素支持 { name, url, size } 对象或纯 url 字符串；
 *    组件只回传「已上传成功」的文件，上传中的临时 blob 地址不会写入。
 *    文件体积（单位 KB，保留两位小数）的保留方式：
 *    - 数组 v-model：写在每项的 size 字段里，随 v-model 一起回传；
 *    - 字符串 v-model：url 装不下体积，改用 size-change 事件抛出（无文件时为 0）。
 * 4. 内置默认上传地址 actionUrl = /adminapi/v1/basicTools/upload/one?uploadImageType=3
 *    （同 UploadFile.vue），最终 action = $util.domain + actionUrl；
 *    默认 name="uploadFile"、with-credentials=true，均可通过传同名属性覆盖。
 * 5. 未自定义插槽内容时，内置拖拽/点击上传的默认 UI（picture-card 时为加号）。
 * 6. limit：最大上传数量，默认 1（不传只能传一张），<=0 表示不限制。
 *    达到上限后隐藏上传入口，并在 before-upload 兜底拦截（防多选/并发绕过）。
 *    注意：这是组件自己实现的，el-upload 1.4 原生没有 limit / on-exceed。
 *
 * 注意：el-upload 1.4 没有 before-remove，也没有原生 limit，
 * 个数限制统一由本组件的 limit 处理。
 * 后端响应约定：{ error: 10000|0, message, data }，data 为文件 url。
 */
const KB = 1024;
// 需要被组件内部接管（并合并外部同名回调）的 el-upload 函数属性
const INTERCEPT_PROPS = [
  "before-upload",
  "on-success",
  "on-error",
  "on-remove",
];

// el-upload 的布尔属性：无值写法（<SUpload drag>）传进来是空串，需转成 true
const BOOLEAN_PROPS = [
  "drag",
  "dragger",
  "with-credentials",
  "show-file-list",
  "multiple",
  "auto-upload",
  "disabled",
];

export default {
  name: "SUpload",
  // 属性由 uploadProps 统一转发给 el-upload，不落到根元素上
  inheritAttrs: false,

  props: {
    /**
     * v-model：支持两种形态，输出类型与输入保持一致
     * - 字符串：单图场景（如 topicForm.coverImage），回传 url 字符串
     * - 数组：多文件场景，元素为 { name, url } 或 url 字符串
     *
     * 默认值为空串（单图模式）而不是 [] ——
     * 父组件字段为 undefined 时 default 会生效，若默认 [] 会被判成数组模式，
     * 出现「v-model 绑的是字符串、回传却是数组」且之后再也切不回字符串的问题。
     */
    value: {
      type: [Array, String],
      default: "",
    },
    size: {
      type: Number,
      default: 0,
    },
    /**
     * 最大上传数量（el-upload 1.4 无 limit，由组件自行实现）。
     * 默认 1（不传则只能传一张）；<=0 表示不限制。
     * 达到上限后隐藏上传入口，并在 before-upload 兜底拦截。
     */
    limit: {
      type: Number,
      default: 1,
    },
    /**
     * 上传地址（相对路径），最终 action = $util.domain + actionUrl。
     * 默认沿用 UploadFile.vue 的通用上传地址；直接传 action 属性可完全覆盖。
     */
    actionUrl: {
      type: String,
      default: "/adminapi/v1/basicTools/upload/one?uploadImageType=3",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // 传给 el-upload 的 file-list：仅在「外部改值」时重建，避免冲掉上传中的文件
      innerFileList: [],
      // 当前文件列表（始终与最新值一致），供内置缩略图回显使用
      currentList: [],
      // 内置预览弹窗
      previewDialog: {
        visible: false,
        url: "",
        name: "",
      },
      lastEmitted: null,
      _suppressRemove: false,
    };
  },

  computed: {
    /**
     * 兜底：$attrs / $listeners 正常挂载时必然存在，
     * 但个别场景（手动 new 组件、异常渲染时机）可能是 undefined，
     * 直接取属性会抛 "Cannot read properties of undefined"，这里统一兜底成空对象。
     */
    safeAttrs() {
      return this.$attrs || {};
    },
    safeListeners() {
      return this.$listeners || {};
    },
    // list-type="picture-card" 时默认只显示一个加号
    isPictureCard() {
      const listType = this.safeAttrs["list-type"] || this.safeAttrs.listType;
      return listType === "picture-card";
    },
    isDrag() {
      return (
        this.safeAttrs.drag !== undefined ||
        this.safeAttrs.dragger !== undefined ||
        this.safeAttrs.type === "drag"
      );
    },
    // 是否展示 el-upload 自带文件列表（默认 true）。关闭时改由内置缩略图回显
    showFileList() {
      const raw =
        this.safeAttrs["show-file-list"] !== undefined
          ? this.safeAttrs["show-file-list"]
          : this.safeAttrs.showFileList;
      if (raw === undefined) {
        return true;
      }
      // 无值写法 <SUpload show-file-list> 传进来是空串
      return raw === "" ? true : !!raw;
    },
    /**
     * 回传模式：跟随 v-model 的输入类型。
     * **只有显式传入数组才按数组模式**；字符串 / null / undefined 一律按单图（字符串）模式。
     *
     * 不能用 `typeof value === "string"` 判断：父组件字段为 null / undefined 时该判断为 false，
     * 会走数组分支把数组回写进去，字段从此变成数组、再也切不回字符串。
     */
    isStringMode() {
      return !Array.isArray(this.value);
    },
    /**
     * 生效的数量上限：字符串 v-model 只能承载一个 url，强制为 1，
     * 否则多选时多余的会被静默丢弃。
     */
    effectiveLimit() {
      if (this.isStringMode) {
        return 1;
      }
      return Number(this.limit) || 0;
    },
    // 已达上限（只依赖响应式 data，不读 $refs，避免依赖收集失效）
    isLimitReached() {
      return (
        this.effectiveLimit > 0 &&
        this.currentList.length >= this.effectiveLimit
      );
    },
    /**
     * 受控用法判定：外部监听了 input（v-model 或 @input）。
     * 只写 :value 而不监听 input 属于非受控用法，此时 prop 仅作初始值，
     * 不能反过来覆盖组件内部状态。
     */
    isControlled() {
      return !!(this.safeListeners && this.safeListeners.input);
    },
    uploadProps() {
      const domain = (this.$util && this.$util.domain) || "";
      // 项目约定默认值：domain + actionUrl、字段名 uploadFile、携带 cookie
      // 外部通过 $attrs 传入的同名属性会覆盖这里的默认值
      const defaults = {
        action: domain + this.actionUrl,
        name: "uploadFile",
        "with-credentials": true,
      };
      // 兼容本仓库常见的 type="drag" 写法（el-upload 1.4 实际只认 drag）
      if (
        this.safeAttrs.type === "drag" &&
        this.safeAttrs.drag === undefined &&
        this.safeAttrs.dragger === undefined
      ) {
        defaults.drag = true;
      }
      const props = { ...defaults, ...this.safeAttrs };
      BOOLEAN_PROPS.forEach((key) => {
        if (key in props && props[key] === "") {
          props[key] = true;
        }
      });
      props["file-list"] = this.innerFileList;
      // 显式绑定 el-upload 的每个回调 prop，不用 v-on="object"，避免触发渲染/事件合并的兼容问题
      props["before-upload"] = this.handleBeforeUpload;
      props["on-success"] = this.handleSuccess;
      props["on-error"] = this.handleError;
      props["on-remove"] = this.handleRemove;
      props["on-change"] = this.handleChange;
      props["on-progress"] = this.handleProgress;
      props["on-preview"] = this.handlePreview;
      return props;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        const next = this.normalizeList(val);
        // 自己 emit 引起的回写直接忽略：
        // 否则会重设 el-upload 的 file-list，把「上传中」的文件从内部列表冲掉
        if (this.isSameList(next, this.lastEmitted)) {
          return;
        }
        /**
         * 非受控用法（只传 :value、未监听 input）：
         * 父组件每次重渲染都会下发新的数组引用（如 :value="[imgUrl]"），
         * 若照单全收会把刚上传成功的文件清空，导致预览消失、limit 判定失效。
         * 此时 prop 只作为初始值 / 显式赋值，空值不参与覆盖。
         */
        if (!this.isControlled && next.length === 0) {
          return;
        }
        this.innerFileList = next;
        this.currentList = next;
        this.lastEmitted = next;
      },
    },
  },

  methods: {
    normalizeList(val) {
      // 单图模式：v-model 直接是 url 字符串
      if (typeof val === "string") {
        return val ? [{ name: this.nameFromUrl(val), url: val }] : [];
      }
      if (!Array.isArray(val)) {
        return [];
      }
      return val
        .map((item) => {
          if (typeof item === "string") {
            return { name: this.nameFromUrl(item), url: item };
          }
          if (item && typeof item === "object") {
            return { ...item };
          }
          return null;
        })
        .filter((item) => item && item.url);
    },
    nameFromUrl(url) {
      const str = String(url || "");
      const last = str.split("/").pop() || "";
      return last.split("?")[0] || str;
    },
    isSameList(a, b) {
      if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
        return false;
      }
      return a.every((item, i) => item.url === b[i].url);
    },

    // 外部传入的同名回调：v-bind 落在 $attrs，v-on 落在 $listeners
    getOriginCallback(name) {
      const fromAttr = this.safeAttrs[name];
      if (typeof fromAttr === "function") {
        return fromAttr;
      }
      const fromListener = this.safeListeners[name];
      if (typeof fromListener === "function") {
        return fromListener;
      }
      if (Array.isArray(fromListener) && fromListener.length) {
        return fromListener[0];
      }
      return null;
    },

    isSuccess(res) {
      return !!res && (res.error === 10000 || res.error === 0);
    },
    // 取文件最终 url：上传成功优先取接口返回，否则取原始 url（排除本地 blob 预览地址）
    pickUrl(item) {
      if (!item) {
        return "";
      }
      if (item.response) {
        if (!this.isSuccess(item.response)) {
          return "";
        }
        const data = item.response.data;
        if (typeof data === "string") {
          return data.trim();
        }
        if (data && typeof data.url === "string") {
          return data.url.trim();
        }
        return "";
      }
      const url = item.url || "";
      return url.indexOf("blob:") === 0 ? "" : url;
    },
    /**
     * 统一出口：更新内部状态并按 v-model 的类型回传
     * 字符串 v-model -> 回传 url 字符串（无文件时为空串）；数组 v-model -> 回传数组
     */
    applyList(next) {
      let list = Array.isArray(next) ? next : [];
      /**
       * 字符串 v-model 只能承载一个 url。
       * 兜底：万一 el-upload 内部列表仍残留历史项，这里只保留最后一项
       * （最后一项才是刚上传成功的），否则会回传/回显成上一张图。
       */
      if (this.isStringMode && list.length > 1) {
        list = list.slice(-1);
      }
      this.currentList = list;
      this.lastEmitted = list;
      if (this.isStringMode) {
        this.$emit("input", list.length ? list[0].url : "");
        /**
         * 字符串 v-model 只能承载一个 url，装不下体积，
         * 因此单独抛 size-change（单位 KB，无文件时为 0），父组件按需监听。
         */
        this.$emit("size-change", list.length ? list[0].size || 0 : 0);
      } else {
        // 数组模式：体积放在每项的 size 字段里随 v-model 一起回传
        this.$emit("input", list.slice());
      }
    },
    /**
     * 取文件体积，单位 KB（保留两位小数）。
     *
     * 两种来源要区分，否则会被重复换算：
     * - 真实上传的文件：uploadFiles 项带 raw，raw.size 是**字节**，需 /1024；
     * - 由 file-list 还原的项（回显/上次上传留下的）：没有 raw，其 size 已经是 KB，原样保留。
     */
    pickSizeKb(item) {
      if (item && item.raw && typeof item.raw.size === "number") {
        return Math.round((item.raw.size / 1024) * 100) / 100;
      }
      if (item && typeof item.size === "number") {
        return item.size;
      }
      return null;
    },
    syncFromUploadFiles(fileList) {
      const next = [];
      (fileList || []).forEach((item) => {
        const url = this.pickUrl(item);
        if (!url) {
          return;
        }
        const entry = { name: item.name || this.nameFromUrl(url), url };
        // 保留文件体积（KB）：数组模式下随 v-model 回传给父组件
        const sizeKb = this.pickSizeKb(item);
        if (sizeKb !== null) {
          entry.size = sizeKb;
        }
        next.push(entry);
      });
      this.applyList(next);
    },
    /**
     * 内置缩略图：删除某一项
     *
     * 坑：达到 limit 时整个 el-upload 会被 v-if 销毁，此时 $refs.upload 为 undefined，
     * 只 splice uploadFiles 是无效的；删除后 el-upload 重新挂载，又会拿 file-list
     * （即 innerFileList）重新初始化 uploadFiles —— 如果 innerFileList 还是删除前的列表，
     * 旧文件会「复活」，表现为：删除后重传，回显/回传仍是上一张图，currentList 出现 2 项。
     * 所以这里必须同步 innerFileList。
     */
    removeAt(index) {
      const target = this.currentList[index];
      const next = this.currentList.slice();
      next.splice(index, 1);

      const upload = this.$refs.upload;
      const files = (upload && upload.uploadFiles) || [];
      if (target) {
        const idx = files.findIndex(
          (item) => this.pickUrl(item) === target.url
        );
        if (idx !== -1) {
          files.splice(idx, 1);
        }
      }
      // 有文件正在上传时不要重设 file-list：会触发 el-upload 的 fileList watch，
      // 把 uploadFiles 整个重建，正在上传的文件会被冲掉（此时 splice 已生效，无需重设）
      const hasUploading = files.some(
        (item) => item.status === "ready" || item.status === "uploading"
      );
      if (!upload || !hasUploading) {
        this.innerFileList = next;
      }
      this.applyList(next);
    },
    /**
     * 内置缩略图：点击预览。
     * 外部传了 on-preview 就交回外部处理（保持 el-upload 语义）；
     * 否则兜底打开内置弹窗，避免点了没反应。
     */
    handlePreviewItem(item) {
      const origin = this.getOriginCallback("on-preview");
      if (typeof origin === "function") {
        origin(item);
        return;
      }
      if (!item || !item.url) {
        return;
      }
      this.previewDialog.url = item.url;
      this.previewDialog.name = item.name || "";
      this.previewDialog.visible = true;
    },

    handleBeforeUpload(file) {
      const checkLimit = () => {
        const limit = this.effectiveLimit;
        if (limit <= 0) {
          return true;
        }
        // 视觉上会隐藏上传入口，但多选/并发上传仍可能绕过，这里兜底拦截。
        // 注意：el-upload 先执行 handleStart（文件已以 ready 状态进入 uploadFiles）
        // 再调用 beforeUpload，所以必须把「当前这个文件」排除掉，否则永远差 1 个额度。
        const files =
          (this.$refs.upload && this.$refs.upload.uploadFiles) || [];
        const isSelf = (item) =>
          (file && item.raw && item.raw === file) ||
          (file && file.uid != null && item.uid === file.uid);
        const inflight = files.filter(
          (item) =>
            (item.status === "ready" || item.status === "uploading") &&
            !isSelf(item)
        ).length;
        if (this.currentList.length + inflight >= limit) {
          this.$message.warning(`最多上传 ${limit} 个文件`);
          return false;
        }
        return true;
      };
      const checkSize = () => {
        const limit = Number(this.size);
        if (!limit || limit <= 0) {
          return true;
        }
        if (file && file.size > limit * KB) {
          this.$message.warning(`文件大小不能超过 ${limit}KB`);
          return false;
        }
        return true;
      };
      // 校验顺序：数量 -> 大小
      const checkOwn = () => (checkLimit() === false ? false : checkSize());
      const origin = this.getOriginCallback("before-upload");
      if (typeof origin === "function") {
        const res = origin(file);
        // 外部明确拒绝则直接中断
        if (res === false) {
          return false;
        }
        // 支持外部返回 Promise
        if (res && typeof res.then === "function") {
          return res.then((r) => (r === false ? false : checkOwn()));
        }
      }
      return checkOwn();
    },

    handleSuccess(res, file, fileList) {
      if (!this.isSuccess(res)) {
        this.$message.error((res && res.message) || "上传失败");
        if (file && this.$refs.upload) {
          // 业务失败：从 el-upload 内部列表移除，避免留下一条“假成功”
          this._suppressRemove = true;
          try {
            this.$refs.upload.handleRemove(file);
          } catch (e) {
            this._suppressRemove = false;
          }
          this._suppressRemove = false;
          return; // handleRemove 内部已完成同步
        }
      }
      this.syncFromUploadFiles(fileList);
      const origin = this.getOriginCallback("on-success");
      if (typeof origin === "function") {
        origin(res, file, fileList);
      }
    },

    handleError(err, file, fileList) {
      if (err && err.status === 403) {
        if (this.$store) {
          this.$store.dispatch("SIGNOUT");
        }
        location.href = "/#";
        return;
      }
      this.$message.error("上传失败");
      this.syncFromUploadFiles(fileList);
      const origin = this.getOriginCallback("on-error");
      if (typeof origin === "function") {
        origin(err, file, fileList);
      }
    },

    handleRemove(file, fileList) {
      this.syncFromUploadFiles(fileList);
      // 上传失败时由组件内部触发的移除，不打扰外部的 on-remove
      if (this._suppressRemove) {
        return;
      }
      const origin = this.getOriginCallback("on-remove");
      if (typeof origin === "function") {
        origin(file, fileList);
      }
    },

    /** 透传 el-upload 1.4 的 on-change 回调 */
    handleChange(file, fileList) {
      const origin = this.getOriginCallback("on-change");
      if (typeof origin === "function") {
        origin(file, fileList);
      }
    },
    /** 透传 el-upload 1.4 的 on-progress 回调 */
    handleProgress(event, file, fileList) {
      const origin = this.getOriginCallback("on-progress");
      if (typeof origin === "function") {
        origin(event, file, fileList);
      }
    },
    /** 透传 el-upload 1.4 的 on-preview 回调（外部未传时由内置缩略图兜底预览） */
    handlePreview(file) {
      const origin = this.getOriginCallback("on-preview");
      if (typeof origin === "function") {
        origin(file);
      }
    },

    // 对外暴露 el-upload 的常用方法，便于手动上传 / 清空
    submit() {
      return this.$refs.upload && this.$refs.upload.submit();
    },
    clearFiles() {
      // 同步清空 file-list，否则 el-upload 重建时会把旧文件带回来
      this.innerFileList = [];
      this.applyList([]);
      return this.$refs.upload && this.$refs.upload.clearFiles();
    },
    abort() {
      return this.$refs.upload && this.$refs.upload.abort();
    },
  },
};
</script>

<style scoped>
/* 内置默认触发内容（未自定义插槽时生效），样式对齐 UploadFile.vue 的拖拽区 */
.s-upload-default {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #97a8be;
  cursor: pointer;
}

.s-upload {
  width: 100%;
  height: 100%;
}

.el-icon-upload {
  margin-bottom: 12px;
  margin-top: 0;
  font-size: 40px;
}

.s-upload-default__text {
  padding: 0 20px;
  color: #97a8be;
  font-size: 12px;
  line-height: 1.5;
  text-align: center;
}

.s-upload-default__link {
  color: #20a0ff;
}

/* tip 提示文案：经 tip 插槽透传后统一排版 */
>>> .el-upload__tip {
  margin-top: 7px;
  color: #8391a5;
  font-size: 12px;
  line-height: 1.4;
}

/* 内置缩略图回显（show-file-list 为 false 时生效） */
.s-upload-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
}

.s-upload-preview__item {
  position: relative;
  width: 140px;
  height: 140px;
  overflow: hidden;
  border: 1px solid #c0ccda;
  border-radius: 4px;
}

.s-upload-preview__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.s-upload-preview__actions {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  height: 100%;
  color: #fff;
  font-size: 18px;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
}

.s-upload-preview__item:hover .s-upload-preview__actions {
  opacity: 1;
}

/* 内置预览弹窗 */
.s-upload-preview-body {
  text-align: center;
  min-height: 200px;
}

.s-upload-preview-body img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}
</style>

<!--
  非 scoped：需要命中 el-upload 的内部结构（.el-upload / .el-dragger）。
  scoped 会把 data-v 属性加在选择器末尾元素上，而 el-upload 内部元素没有该属性，
  规则永远匹配不到 —— 故沿用 UploadImg.vue 的独立非 scoped 写法。
  选择器以 .s-upload-preview 开头，不会污染其他组件。
-->
<style>
/* 根节点为 .s-upload-preview（不是 .s-upload），下面选择器需与之对应 */
.s-upload-preview .el-upload {
  width: 100%;
  height: 100%;
}

.s-upload-preview__item .el-upload-dragger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
}

.s-upload-preview .el-upload--text {
  width: 100%;
  height: 100%;
}

/* 上传入口内部内容居中 */
.s-upload-trigger__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 8px;
  color: #97a8be;
}

.s-upload-trigger__inner .el-icon-upload {
  margin: 0 0 6px;
  font-size: 28px;
  line-height: 1;
}

.s-upload-trigger__inner .s-upload-default__text {
  padding: 0 8px;
  color: #97a8be;
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// SUpload.vue?5b05f110